import { Breakpoint, Typography } from '@altibox/design-system-component-lib';
import React, { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Container } from 'app/components/container';
import { i18n } from 'app/i18n';
import { HouseType } from 'app/pages/order/model/house-type';
import { OrderStep } from 'app/pages/order/redux/model/order-step';
import { selectHasCustomerDebt, selectPartner, selectShoppingCartOrThrow } from 'app/pages/order/redux/order-selector';
import { useAppDispatch } from 'app/hooks/thunk-hooks';
import { selectHouseTypeActionCreator } from 'app/pages/order/redux/action-creators/select-house-type-action-creator';
import { changeIsPurchasingSingleUnitActionCreator } from 'app/pages/order/redux/action-creators/change-is-purchasing-single-unit-action-creator';
import { I18nUtils } from 'app/utils/i18n-utils';
import { Link } from 'app/components/link';
import { config } from 'app/config/config';
import { useGoToStep } from 'app/hooks/use-go-to-step';
import { pushViewItemListEvent } from 'app/data-layers/events/push-view-item-list-event';

import {
    StyledBuildingNote,
    StyledBuildingSelectCard,
    StyledBuildingSelectCardContainer,
    StyledContainer,
    StyledCustomerDebtAlert,
    StyledHomeGatewayUpgradeAlert,
    StyledSingleUnitCard,
    StyledSingleUnitTitle,
    StyledSubTitle,
    StyledTitle,
} from './housing-select-page-content.styled';

export const HousingSelectPageContent: FunctionComponent = () => {
    const customerHasDebt = useSelector(selectHasCustomerDebt);
    const shoppingCart = useSelector(selectShoppingCartOrThrow);
    const partner = useSelector(selectPartner);
    const { homeGatewayUpgrade } = shoppingCart;
    const dispatch = useAppDispatch();
    const goToStep = useGoToStep();

    useEffect(() => {
        pushViewItemListEvent(shoppingCart, partner);
    }, [partner, shoppingCart]);

    const handleBuildingSelect = (houseType: HouseType) => {
        dispatch(selectHouseTypeActionCreator(houseType));
        goToStep(OrderStep.PRODUCTS_DETAILS);
    };

    const handleSingleUnitClick = () => {
        dispatch(changeIsPurchasingSingleUnitActionCreator(true));
        goToStep(OrderStep.SUMMARY);
    };

    return (
        <>
            <Container>
                <StyledTitle variant="headline2" component="h1">
                    {i18n.page.housingSelect.title}
                </StyledTitle>
                <StyledSubTitle variant="headline4" component="h2">
                    {i18n.page.housingSelect.subTitle}
                </StyledSubTitle>
                <StyledBuildingSelectCardContainer>
                    <StyledBuildingSelectCard
                        houseType={HouseType.MEDIUM_HOUSE}
                        onButtonClick={() => handleBuildingSelect(HouseType.MEDIUM_HOUSE)}
                    />
                    <StyledBuildingSelectCard
                        houseType={HouseType.LARGE_HOUSE}
                        onButtonClick={() => handleBuildingSelect(HouseType.LARGE_HOUSE)}
                    />
                </StyledBuildingSelectCardContainer>
                <StyledBuildingNote variant="paragraph3" component="p">
                    {i18n.page.housingSelect.buildingNote}
                </StyledBuildingNote>
                <Typography variant="paragraph3" component="p">
                    {I18nUtils.richMessage(i18n.page.housingSelect.getMoreInformation, {
                        link: (
                            <Link variant="paragraph3" href={config.ABOUT_WIFI_PAGE_URL}>
                                {i18n.page.housingSelect.linkToMoreInformation}
                            </Link>
                        ),
                    })}
                </Typography>
                {homeGatewayUpgrade && !customerHasDebt && <StyledHomeGatewayUpgradeAlert />}
                {customerHasDebt && <StyledCustomerDebtAlert />}
            </Container>
            <StyledContainer>
                <Container>
                    <StyledSingleUnitTitle variant="headline5" component="h3" maxBreakpoint={Breakpoint.TABLET}>
                        {i18n.page.housingSelect.singleUnitTitle}
                    </StyledSingleUnitTitle>
                    <StyledSingleUnitCard onButtonClick={handleSingleUnitClick} disabled={customerHasDebt} />
                </Container>
            </StyledContainer>
        </>
    );
};
